import Cookies from 'js-cookie'
import { handleSessionExpired } from '@/lib/authToken'
import type { AdminUser, AdminWorkspace, AdminMember, AdminUserWorkspace, AuditLog, AiToolCallEvent, AccountEvent, SessionSummary, PaginatedResponse, PlanRecord } from '../types/index.js'

const API_BASE = (import.meta.env?.VITE_API_URL as string) || ''

function buildUrl(path: string) {
  if (API_BASE) {
    return `${API_BASE.replace(/\/+$/, '')}/${path.replace(/^\/+/, '')}`
  }
  return path.startsWith('/') ? path : `/${path}`
}

async function requestVoid(method: string, path: string, body?: unknown) {
  const token = Cookies.get('token')
  const url = buildUrl(path)
  const headers: Record<string, string> = {}
  if (token) headers.Authorization = `Bearer ${token}`

  const init: RequestInit = { method, headers }
  if (body !== undefined) init.body = JSON.stringify(body)

  const res = await fetch(url, init)

  if (!res.ok) {
    if (res.status === 401) {
      handleSessionExpired('/admin/login')
      throw new Error('Session expired')
    }
    const payload = await res.json().catch(() => null)
    let msg: string | undefined = undefined
    if (payload) {
      if (typeof payload === 'string') msg = payload
      else if (typeof payload.message === 'string') msg = payload.message
      else if (typeof payload.error === 'string') msg = payload.error
      else if (typeof payload.error === 'object' && payload.error !== null) {
        const details = Array.isArray(payload.error.details) ? payload.error.details : null
        msg = details?.length
          ? details.map((d: { field?: string; message?: string }) => d.message || d.field).join('; ')
          : (payload.error.message as string) || (payload.error.code as string)
      } else if (typeof payload.code === 'string') msg = payload.code
    }
    throw new Error(msg || res.statusText || 'Request failed')
  }
}

async function requestJson(method: string, path: string, body?: unknown) {
  const token = Cookies.get('token')
  const url = buildUrl(path)
  const headers: Record<string, string> = {}
  if (body !== undefined) headers['Content-Type'] = 'application/json'
  if (token) headers.Authorization = `Bearer ${token}`

  const init: RequestInit = { method, headers }
  if (body !== undefined) init.body = JSON.stringify(body)

  const res = await fetch(url, init)

  if (!res.ok) {
    if (res.status === 401) {
      handleSessionExpired('/admin/login')
      throw new Error('Session expired')
    }
    const payload = await res.json().catch(() => null)
    let msg: string | undefined = undefined
    if (payload) {
      if (typeof payload === 'string') msg = payload
      else if (typeof payload.message === 'string') msg = payload.message
      else if (typeof payload.error === 'string') msg = payload.error
      else if (typeof payload.error === 'object' && payload.error !== null) {
        const details = Array.isArray(payload.error.details) ? payload.error.details : null
        msg = details?.length
          ? details.map((d: { field?: string; message?: string }) => d.message || d.field).join('; ')
          : (payload.error.message as string) || (payload.error.code as string)
      } else if (typeof payload.code === 'string') msg = payload.code
    }
    msg = msg || res.statusText || 'Request failed'
    throw new Error(msg)
  }

  return res.json() as Promise<unknown>
}

// Auth

export type AdminAuthResult = {
  access_token: string
  user: { id: string; email: string; name: string; platformRole: string }
}

export async function adminRegister(payload: {
  name: string
  email: string
  password: string
  confirm_password: string
}): Promise<AdminAuthResult> {
  return requestJson('POST', '/api/admin/auth/register', payload) as Promise<AdminAuthResult>
}

// Users

export async function listUsers(
  page = 1,
  limit = 20,
): Promise<PaginatedResponse<AdminUser>> {
  return requestJson(
    'GET',
    `/api/admin/users?page=${page}&limit=${limit}`,
  ) as Promise<PaginatedResponse<AdminUser>>
}

export async function getUser(id: string): Promise<AdminUser> {
  return requestJson('GET', `/api/admin/users/${id}`) as Promise<AdminUser>
}

export async function updateUser(
  id: string,
  payload: Partial<{ name: string; email: string; platformRole: 'user' | 'super_admin'; isActive: boolean }>,
): Promise<AdminUser> {
  return requestJson('PATCH', `/api/admin/users/${id}`, payload) as Promise<AdminUser>
}

export async function resetUserPassword(
  userId: string,
  payload: { password: string; confirm_password: string },
): Promise<AdminUser> {
  return requestJson('POST', `/api/admin/users/${userId}/reset-password`, payload) as Promise<AdminUser>
}

export async function addUserToWorkspace(
  userId: string,
  payload: { workspaceId: string; role: 'owner' | 'admin' | 'member' },
): Promise<AdminMember> {
  return requestJson('POST', `/api/admin/users/${userId}/workspaces`, payload) as Promise<AdminMember>
}

export async function removeUserFromWorkspace(userId: string, membershipId: string): Promise<void> {
  await requestVoid('DELETE', `/api/admin/users/${userId}/workspaces/${membershipId}`)
}

export async function updateUserMembershipRole(
  userId: string,
  membershipId: string,
  role: 'owner' | 'admin' | 'member',
): Promise<AdminMember> {
  return requestJson('PATCH', `/api/admin/users/${userId}/workspaces/${membershipId}`, { role }) as Promise<AdminMember>
}

// Workspaces

export async function listWorkspaces(
  page = 1,
  limit = 20,
): Promise<PaginatedResponse<AdminWorkspace>> {
  return requestJson(
    'GET',
    `/api/admin/workspaces?page=${page}&limit=${limit}`,
  ) as Promise<PaginatedResponse<AdminWorkspace>>
}

export async function getWorkspace(id: string): Promise<AdminWorkspace> {
  return requestJson('GET', `/api/admin/workspaces/${id}`) as Promise<AdminWorkspace>
}

export async function updateWorkspace(
  id: string,
  payload: Partial<{ isActive: boolean }>,
): Promise<AdminWorkspace> {
  return requestJson('PATCH', `/api/admin/workspaces/${id}`, payload) as Promise<AdminWorkspace>
}

export async function getWorkspaceMembers(id: string): Promise<AdminMember[]> {
  return requestJson('GET', `/api/admin/workspaces/${id}/members`) as Promise<AdminMember[]>
}

export async function getUserWorkspaces(id: string): Promise<AdminUserWorkspace[]> {
  return requestJson('GET', `/api/admin/users/${id}/workspaces`) as Promise<AdminUserWorkspace[]>
}

// Plans

export async function listPlans(): Promise<PlanRecord[]> {
  return requestJson('GET', '/api/admin/plans') as Promise<PlanRecord[]>
}

export async function createPlan(input: {
  name: string
  seats: number
  monthlyAiCredits: number
}): Promise<PlanRecord> {
  return requestJson('POST', '/api/admin/plans', input) as Promise<PlanRecord>
}

export async function updatePlan(
  id: string,
  input: Partial<{ name: string; seats: number; monthlyAiCredits: number }>,
): Promise<PlanRecord> {
  return requestJson('PATCH', `/api/admin/plans/${id}`, input) as Promise<PlanRecord>
}

export async function deletePlan(id: string): Promise<void> {
  await requestVoid('DELETE', `/api/admin/plans/${id}`)
}

export async function assignWorkspacePlan(workspaceId: string, planId: string | null): Promise<void> {
  await requestVoid('PATCH', `/api/admin/workspaces/${workspaceId}/plan`, { planId })
}

// AI Config

export type SystemPromptResponse = { value: string; defaultValue: string }
export type UpdateSystemPromptResponse = { value: string; updatedAt: string }

export async function getSystemPrompt(): Promise<SystemPromptResponse> {
  return requestJson('GET', '/api/admin/config/system-prompt') as Promise<SystemPromptResponse>
}

export async function updateSystemPrompt(value: string): Promise<UpdateSystemPromptResponse> {
  return requestJson('PUT', '/api/admin/config/system-prompt', { value }) as Promise<UpdateSystemPromptResponse>
}

// Audit logs

export async function listAuditLogs(
  page = 1,
  limit = 20,
): Promise<PaginatedResponse<AuditLog>> {
  return requestJson(
    'GET',
    `/api/admin/logs?page=${page}&limit=${limit}`,
  ) as Promise<PaginatedResponse<AuditLog>>
}

// AI activity stream

export type AiActivitySseEvent =
  | { type: 'tool_call'; event: AiToolCallEvent }
  | { type: 'error'; message: string }

async function consumeAiActivitySse(
  path: string,
  onEvent: (event: AiActivitySseEvent) => void,
  signal?: AbortSignal,
): Promise<void> {
  const token = Cookies.get('token')
  const headers: Record<string, string> = { Accept: 'text/event-stream' }
  if (token) headers['Authorization'] = `Bearer ${token}`

  const res = await fetch(buildUrl(path), {
    method: 'GET',
    headers,
    ...(signal ? { signal } : {}),
  })

  if (!res.ok) {
    const payload = await res.json().catch(() => null)
    let msg = payload?.error?.message || payload?.message || res.statusText || 'Stream failed'
    if (typeof msg !== 'string') msg = 'Stream failed'
    throw new Error(msg)
  }

  const reader = res.body!.getReader()
  const decoder = new TextDecoder()
  let buffer = ''

  while (true) {
    const { done, value } = await reader.read()
    if (done) break

    buffer += decoder.decode(value, { stream: true })
    const blocks = buffer.split('\n\n')
    buffer = blocks.pop() ?? ''

    for (const block of blocks) {
      const lines = block.split('\n')
      let eventType = ''
      let data = ''
      for (const line of lines) {
        if (line.startsWith('event: ')) eventType = line.slice(7).trim()
        else if (line.startsWith('data: ')) data = line.slice(6).trim()
      }
      if (!eventType || !data) continue
      try {
        const parsed = JSON.parse(data)
        switch (eventType) {
          case 'tool_call': onEvent({ type: 'tool_call', event: parsed }); break
          case 'error': onEvent({ type: 'error', message: parsed.message }); break
        }
      } catch {
        // ignore malformed SSE blocks
      }
    }
  }
}

export async function streamWorkspaceAiActivity(
  workspaceId: string,
  onEvent: (event: AiActivitySseEvent) => void,
  signal?: AbortSignal,
): Promise<void> {
  return consumeAiActivitySse(`api/admin/workspaces/${workspaceId}/ai-activity/stream`, onEvent, signal)
}

// Per-user activity logs (account events + live AI sessions)

export async function getUserAccountEvents(userId: string): Promise<AccountEvent[]> {
  const result = await requestJson('GET', `/api/admin/users/${userId}/account-events`) as { data: AccountEvent[] }
  return result.data
}

export async function streamUserAiActivity(
  userId: string,
  onEvent: (event: AiActivitySseEvent) => void,
  signal?: AbortSignal,
): Promise<void> {
  return consumeAiActivitySse(`api/admin/users/${userId}/ai-activity/stream`, onEvent, signal)
}

export type CursorPage<T> = { data: T[]; nextCursor: string | null }

export async function listUserSessions(userId: string, cursor?: string | null, limit = 20): Promise<CursorPage<SessionSummary>> {
  const params = new URLSearchParams({ limit: String(limit) })
  if (cursor) params.set('cursor', cursor)
  return requestJson('GET', `/api/admin/users/${userId}/sessions?${params.toString()}`) as Promise<CursorPage<SessionSummary>>
}

export async function listUserSessionToolCalls(
  userId: string,
  conversationId: string,
  cursor?: string | null,
  limit = 25,
): Promise<CursorPage<AiToolCallEvent>> {
  const params = new URLSearchParams({ limit: String(limit) })
  if (cursor) params.set('cursor', cursor)
  return requestJson(
    'GET',
    `/api/admin/users/${userId}/sessions/${conversationId}/tool-calls?${params.toString()}`,
  ) as Promise<CursorPage<AiToolCallEvent>>
}

// Per-workspace activity logs (account events + live AI sessions)

export async function getWorkspaceAccountEvents(workspaceId: string): Promise<AccountEvent[]> {
  const result = await requestJson('GET', `/api/admin/workspaces/${workspaceId}/account-events`) as { data: AccountEvent[] }
  return result.data
}

export async function listWorkspaceSessions(workspaceId: string, cursor?: string | null, limit = 20): Promise<CursorPage<SessionSummary>> {
  const params = new URLSearchParams({ limit: String(limit) })
  if (cursor) params.set('cursor', cursor)
  return requestJson('GET', `/api/admin/workspaces/${workspaceId}/sessions?${params.toString()}`) as Promise<CursorPage<SessionSummary>>
}

export async function listWorkspaceSessionToolCalls(
  workspaceId: string,
  conversationId: string,
  cursor?: string | null,
  limit = 25,
): Promise<CursorPage<AiToolCallEvent>> {
  const params = new URLSearchParams({ limit: String(limit) })
  if (cursor) params.set('cursor', cursor)
  return requestJson(
    'GET',
    `/api/admin/workspaces/${workspaceId}/sessions/${conversationId}/tool-calls?${params.toString()}`,
  ) as Promise<CursorPage<AiToolCallEvent>>
}
